import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import { buyerEnquiryApi } from '../BuyerEnquiry/BuyerEnquiryService';
import type {
  AddNegotiationNoteArgs,
  CloseNegotiationArgs,
  CreatedNegotiation,
  NegotiationDetail,
  NegotiationListRow,
  NegotiationRow,
  RevealedSeller,
  SubmitCounterOfferArgs,
  SubmitOfferArgs,
  UpdateNegotiationStageArgs,
  UploadNegotiationDocumentArgs
} from './negotiationTypes';

/**
 * The buyer's negotiation surface, `/api/buyer/negotiations/*`.
 *
 * Envelope variance: the list and the detail answer `{ success, message: 'OK', data }`, create
 * and add-note answer 201 `{ data: { id } }`, and every stage transition (stage, offer, counter,
 * close) answers `{ success, message }` with NO `data` key. Normalizers are module-scope, take
 * `unknown` and are TOTAL — they never throw and never return `undefined`.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const envelope = (response: unknown): Record<string, unknown> => asRecord(asRecord(response).data);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

/** An id the UI hands back to the API is never invented — absent means `null`. */
const asId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const asNullableRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

export const normalizeNegotiationList = (response: unknown): NegotiationRow[] => {
  const data = asRecord(response).data;
  const items = Array.isArray(data) ? data : asRecord(data).items;
  return Array.isArray(items) ? items.map(asRecord) : [];
};

/**
 * `revealedSeller` is the seller's real contact details. It is absent — not empty — until the
 * enquiry is approved, so a missing block stays `null` and the three fields keep their own nulls.
 */
export function normalizeNegotiationDetail(response: unknown): NegotiationDetail {
  const data = envelope(response);
  const rawSeller = asNullableRecord(data.revealedSeller);
  const revealedSeller: RevealedSeller | null =
    rawSeller === null
      ? null
      : {
          sellerName: asNullableString(rawSeller.sellerName),
          sellerMobile: asNullableString(rawSeller.sellerMobile),
          sellerEmail: asNullableString(rawSeller.sellerEmail)
        };
  return { negotiation: asNullableRecord(data.negotiation), revealedSeller };
}

export function normalizeCreatedNegotiation(response: unknown): CreatedNegotiation {
  return { id: asId(envelope(response).id) };
}

/** `okMessage` answers `{ success, message }` with no `data`, so only the message is available. */
export function normalizeMessage(response: unknown, fallback = ''): string {
  return asString(asRecord(response).message, fallback);
}

export const negotiationApi = createApi({
  reducerPath: 'negotiationApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['Negotiation', 'NegotiationNote'],
  endpoints: (builder) => ({
    listBuyerNegotiations: builder.query<NegotiationListRow[], void>({
      query: () => ({ url: 'buyer/negotiations' }),
      transformResponse: (response: unknown) =>
        normalizeNegotiationList(response) as NegotiationListRow[],
      providesTags: [{ type: 'Negotiation', id: 'LIST' }]
    }),
    getBuyerNegotiation: builder.query<NegotiationDetail, { id: number }>({
      query: ({ id }) => ({ url: `buyer/negotiations/${id}` }),
      transformResponse: (response: unknown) => normalizeNegotiationDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'Negotiation', id: String(arg.id) }]
    }),
    createBuyerNegotiation: builder.mutation<CreatedNegotiation, { enquiryId: number }>({
      query: (body) => ({ url: 'buyer/negotiations', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeCreatedNegotiation(response),
      invalidatesTags: [{ type: 'Negotiation', id: 'LIST' }],
      /**
       * The enquiry detail embeds the negotiation it spawned, so a new negotiation has to reach
       * `buyerEnquiryApi` — otherwise the enquiry screen keeps offering "start negotiation".
       */
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            buyerEnquiryApi.util.invalidateTags([
              { type: 'BuyerEnquiry' as const, id: String(arg.enquiryId) },
              { type: 'BuyerEnquiry' as const, id: 'LIST' }
            ])
          );
        } catch {
          /* nothing changed server-side */
        }
      }
    }),
    updateNegotiationStage: builder.mutation<string, UpdateNegotiationStageArgs>({
      query: ({ id, stage, note, date }) => ({
        url: `buyer/negotiations/${id}/stage`,
        method: 'PUT',
        body: { stage, note, date }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Negotiation', id: String(arg.id) },
        { type: 'Negotiation', id: 'LIST' }
      ]
    }),
    submitOffer: builder.mutation<string, SubmitOfferArgs>({
      query: ({ id, amount, note }) => ({
        url: `buyer/negotiations/${id}/offer`,
        method: 'POST',
        body: { amount, note }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Negotiation', id: String(arg.id) },
        { type: 'Negotiation', id: 'LIST' }
      ]
    }),
    /** POST `/counter`, PUT `/close` — the verbs differ per route; do not "tidy" them. */
    submitCounterOffer: builder.mutation<string, SubmitCounterOfferArgs>({
      query: ({ id, amount }) => ({
        url: `buyer/negotiations/${id}/counter`,
        method: 'POST',
        body: { amount }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Negotiation', id: String(arg.id) },
        { type: 'Negotiation', id: 'LIST' }
      ]
    }),
    closeNegotiation: builder.mutation<string, CloseNegotiationArgs>({
      query: ({ id, outcome, reason }) => ({
        url: `buyer/negotiations/${id}/close`,
        method: 'PUT',
        body: { outcome, reason }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Negotiation', id: String(arg.id) },
        { type: 'Negotiation', id: 'LIST' }
      ]
    }),
    /** Notes are cached per negotiation id — there is no global note list. */
    listNegotiationNotes: builder.query<NegotiationRow[], { id: number }>({
      query: ({ id }) => ({ url: `buyer/negotiations/${id}/notes` }),
      transformResponse: (response: unknown) => normalizeNegotiationList(response),
      providesTags: (_result, _error, arg) => [{ type: 'NegotiationNote', id: String(arg.id) }]
    }),
    addNegotiationNote: builder.mutation<CreatedNegotiation, AddNegotiationNoteArgs>({
      query: ({ id, note }) => ({
        url: `buyer/negotiations/${id}/notes`,
        method: 'POST',
        body: { note }
      }),
      transformResponse: (response: unknown) => normalizeCreatedNegotiation(response),
      invalidatesTags: (_result, _error, arg) => [{ type: 'NegotiationNote', id: String(arg.id) }]
    }),
    /**
     * FormData body with NO `Content-Type` header: the browser has to set the multipart boundary
     * itself. The multer field is `file`; `documentType` rides along in the same form.
     */
    uploadNegotiationDocument: builder.mutation<string, UploadNegotiationDocumentArgs>({
      query: ({ id, file, documentType }) => {
        const form = new FormData();
        form.append('file', file);
        form.append('documentType', documentType);
        return { url: `buyer/negotiations/${id}/documents`, method: 'POST', body: form };
      },
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Negotiation', id: String(arg.id) }]
    })
  })
});

export const {
  useListBuyerNegotiationsQuery,
  useGetBuyerNegotiationQuery,
  useCreateBuyerNegotiationMutation,
  useUpdateNegotiationStageMutation,
  useSubmitOfferMutation,
  useSubmitCounterOfferMutation,
  useCloseNegotiationMutation,
  useListNegotiationNotesQuery,
  useAddNegotiationNoteMutation,
  useUploadNegotiationDocumentMutation
} = negotiationApi;
